/**
 * design/12-onboarding-and-credentials.md §12.4–§12.6 — stages 2–4, the `a-onboard` wizard.
 * Stages 0–1 (sign-up, e-mail confirmation) are `src/layers/auth/`'s; this wizard begins at the
 * credential (§12.4, "applied W83… stages 0 and 1 are not steps in it").
 *
 * Unlike the auth layer, this is a strictly linear progression — LLD-SPA-046: "the step rail
 * advances only when the preceding step succeeded" — so a plain stage counter plus the
 * accumulated form fields is the whole state; there is no branching to transcribe the way
 * `layers/auth/state.ts` has to.
 *
 * **`BACK` corrected to actually carry the accumulated fields backward.** It returned a bare
 * `{ stage: 1 }`/dropped `workspaceName` when stepping back from stage 2/3, so the field this
 * state already held was thrown away one action before the pane that reads it unmounted — the
 * pane then mounted its own fresh, empty `useState('')` with nothing to seed it from. The stage 1
 * and 2 members below now carry their own field as optional for exactly this reverse direction;
 * forward transitions still always populate it.
 *
 * **`BACK` from stage 4 was a silent no-op — no case matched it at all**, so `ob-wz-4`'s own Back
 * button did nothing. Stage 4 now also carries `credential`/`workspaceName` forward from stage 3,
 * as optional fields, so a return trip re-lands on `ob-wz-3` with both seeded (`workspaceConnect`
 * never returns the raw credential, so there is nothing else to reconstruct it from). They stay
 * optional rather than required because stage 6's own `BACK` (dead in the UI today — `ObWz6` has
 * no back button — kept here for completeness) produces a stage-4 state from `{ workspace }`
 * alone — reachable only after the scope check already passed — and that path has no credential
 * to carry; the 4→3 hop no-ops rather than guesses when it finds neither field.
 *
 * **Stage 5 (repository scope, `ob-wz-5`) removed from the active flow (Sanket, 2026-09-25),
 * found live.** Two real defects, not cosmetic: it showed "0 repositories" for a workspace that
 * had several (`repositoryCount` came from `workspace.repository_count`, which `workspaceConnect`'s
 * response does not populate), and its disabled "choose individual repositories" option rendered
 * `chooseNowUnavailableNote` — copy that says "see the frontend/README.md finding" verbatim, an
 * internal dev note leaking straight into a customer-facing screen. Removing the step rather than
 * patching the copy/count is deliberate: `ob-wz-5`'s only WORKING path was already "all
 * repositories" (§12.5.1: exclusions are opt-out, so an empty `repo_scope_exclusions` — never
 * writing the PUT this pane fired — already means "protect everything," and
 * `auto_protect_new_repositories` already defaults `TRUE` at the DB level per §12.5.2). So
 * `SCOPE_CHECK_PASSED` now goes straight to stage 6 and neither of `ob-wz-5`'s two API calls needs
 * a replacement — the backend defaults already ARE what the removed screen's only real option did.
 * `ObWz5.tsx`/`OB_WZ_5_COPY` are left in place, untouched, for the future pass that builds a real
 * per-repository picker rather than deleted — this is a paused step, not abandoned code.
 *
 * **Structural deviation from the prototype, reported here rather than silently matched.**
 * Prototype_v2.html's `ob-wz-1` validates the credential with its own "Verify and store" call,
 * and `ob-wz-2` validates the slug with its own "Check this workspace" call — two separate
 * platform round trips, matching design/12.4.2's older narrative. **The current contract's
 * `WorkspaceConnect` schema takes `workspace_name`, `credential` and `region` together in ONE
 * call** (`workspaceConnect`, `api/paths/workspace/workspaces.yaml`), with no earlier
 * per-field validation call available. This wizard therefore accumulates the credential (stage
 * 1), the workspace name (stage 2) and the region (stage 3) as plain client state and fires the
 * one real call when stage 3 is submitted — the three panes still exist and still validate
 * their own field's shape client-side, but the platform round trip moved to the end of stage 3.
 * `WorkspaceConnect` also has no separate "Atlassian account email" field the way the prototype's
 * `ob-wz-1` draws one — dropped rather than sent nowhere, and reported for the same reason.
 */
import type { components } from '@/api/generated/schema'

type Workspace = components['schemas']['Workspace']
type Region = components['schemas']['Region']
type JobRef = components['schemas']['JobRef']

export type OnboardingState =
  | { stage: 1; credential?: string }
  | { stage: 2; credential: string; workspaceName?: string }
  | { stage: 3; credential: string; workspaceName: string }
  | { stage: 4; credential?: string; workspaceName?: string; workspace: Workspace }
  | { stage: 6; workspace: Workspace }
  | { stage: 'done'; workspace: Workspace; started: JobRef | null }

export type OnboardingAction =
  | { type: 'BACK' }
  | { type: 'CREDENTIAL_ENTERED'; credential: string }
  | { type: 'WORKSPACE_NAMED'; workspaceName: string }
  | { type: 'CONNECTED'; workspace: Workspace }
  | { type: 'SCOPE_CHECK_PASSED' }
  | { type: 'COMPLETED'; started: JobRef | null }
  | { type: 'RESUMED'; workspace: Workspace }

export function onboardingReducer(state: OnboardingState, action: OnboardingAction): OnboardingState {
  switch (action.type) {
    // §12.6.5's own resume table has four rows; a connected-but-unfinished workspace (the only
    // one this layer can currently be handed — `credential_status: 'PENDING'` is the credential
    // recovery flow, `/credential`, not this wizard) always lands on the scope check regardless of
    // which of the table's last three rows is actually true. Re-running an already-passed scope
    // check is a harmless, idempotent re-check (`ObWz4` always POSTs a fresh run on mount); it is
    // NOT harmless to guess "scope already passed" and jump straight to `ob-wz-5`/`ob-wz-6`,
    // because both of those stages need a `can_continue` this layer has no way to know without
    // running the check.
    case 'RESUMED':
      return { stage: 4, workspace: action.workspace }
    case 'CREDENTIAL_ENTERED':
      return { stage: 2, credential: action.credential }
    case 'WORKSPACE_NAMED':
      return state.stage === 2 ? { stage: 3, credential: state.credential, workspaceName: action.workspaceName } : state
    case 'CONNECTED':
      return state.stage === 3 ? { stage: 4, credential: state.credential, workspaceName: state.workspaceName, workspace: action.workspace } : state
    // Stage 5 (`ob-wz-5`) skipped — see this file's own doc comment.
    case 'SCOPE_CHECK_PASSED':
      return 'workspace' in state ? { stage: 6, workspace: state.workspace } : state
    case 'COMPLETED':
      return 'workspace' in state ? { stage: 'done', workspace: state.workspace, started: action.started } : state
    case 'BACK':
      if (state.stage === 2) return { stage: 1, credential: state.credential }
      if (state.stage === 3) return { stage: 2, credential: state.credential, workspaceName: state.workspaceName }
      if (state.stage === 4) {
        return state.credential !== undefined && state.workspaceName !== undefined
          ? { stage: 3, credential: state.credential, workspaceName: state.workspaceName }
          : state
      }
      if (state.stage === 6) return { stage: 4, workspace: state.workspace }
      return state
    default:
      return state
  }
}

export const initialOnboardingState: OnboardingState = { stage: 1 }

export type { Region }
